import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { classifyTouchAxis } from '../../utils/touchGesture';
import './post.css';

const MOBILE_BREAKPOINT = '(max-width: 767px)';
const EXIT_DURATION = 190;
const KEYBOARD_CLOSE_DELAY = 420;

export function useMobileViewport() {
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia(MOBILE_BREAKPOINT).matches
  ));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT);
    const handleViewportChange = (event) => setIsMobileViewport(event.matches);
    setIsMobileViewport(mediaQuery.matches);
    mediaQuery.addEventListener?.('change', handleViewportChange);
    // Older Android WebViews expose MediaQueryList.addListener instead.
    if (!mediaQuery.addEventListener) mediaQuery.addListener?.(handleViewportChange);
    return () => {
      mediaQuery.removeEventListener?.('change', handleViewportChange);
      if (!mediaQuery.removeEventListener) mediaQuery.removeListener?.(handleViewportChange);
    };
  }, []);

  return isMobileViewport;
}

/**
 * Reuses the Home feed's mobile comments sheet outside the feed. Children
 * remain mounted in the same React tree (so comment state/actions stay owned
 * by their page) while the presentation is portalled above the app shell.
 */
export default function MobileCommentsSheet({ open, onClose, children, className = '' }) {
  const isMobileViewport = useMobileViewport();
  const sheetRef = useRef(null);
  const backdropRef = useRef(null);
  const dragRef = useRef(null);
  const closeTimerRef = useRef(null);
  const closingRef = useRef(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    closingRef.current = false;
    setIsClosing(false);
  }, [open]);

  useEffect(() => {
    if (!open || !isMobileViewport || typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const body = document.body;
    const rootAlreadyLocked = root.classList.contains('gm-home-comments-open');
    const bodyAlreadyLocked = body.classList.contains('gm-home-comments-open');
    root.classList.add('gm-home-comments-open');
    body.classList.add('gm-home-comments-open');

    return () => {
      if (!rootAlreadyLocked) root.classList.remove('gm-home-comments-open');
      if (!bodyAlreadyLocked) body.classList.remove('gm-home-comments-open');
    };
  }, [open, isMobileViewport]);

  useEffect(() => () => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
  }, []);

  const requestClose = useCallback(() => {
    if (!open || closingRef.current) return;
    closingRef.current = true;
    setIsClosing(true);

    const sheet = sheetRef.current;
    const activeElement = document.activeElement;
    const focusedComposer = sheet?.querySelector('.gm-ct__composer-input:focus');
    (focusedComposer || (sheet?.contains(activeElement) ? activeElement : null))?.blur?.();
    const delay = focusedComposer ? KEYBOARD_CLOSE_DELAY : EXIT_DURATION;
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      onClose?.();
    }, delay);
  }, [onClose, open]);

  useEffect(() => {
    if (!open || !isMobileViewport) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        requestClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMobileViewport, open, requestClose]);

  useEffect(() => {
    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;
    if (!open || !isMobileViewport || !sheet) return undefined;

    const clearDragVisuals = () => {
      sheet.classList.remove('gm-post__comments-wrap--dragging');
      sheet.style.removeProperty('--gm-home-comments-drag-y');
      backdrop?.classList.remove('gm-post__comments-backdrop--dragging');
      backdrop?.style.removeProperty('--gm-home-comments-backdrop-opacity');
    };

    const onTouchStart = (event) => {
      if (closingRef.current || event.touches.length !== 1) {
        dragRef.current = null;
        clearDragVisuals();
        return;
      }

      const touch = event.touches[0];
      const target = event.target;
      if (target?.closest?.('button, a, input, textarea, select, [contenteditable="true"], [role="button"]')) {
        dragRef.current = null;
        return;
      }

      const list = target?.closest?.('.gm-ct__list') || null;
      dragRef.current = {
        touchId: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        dragStartY: touch.clientY,
        lastY: touch.clientY,
        lastTime: event.timeStamp,
        velocityY: 0,
        startScrollTop: list ? list.scrollTop : 0,
        list,
        axis: null,
        dragging: false,
      };
    };

    const onTouchMove = (event) => {
      const gesture = dragRef.current;
      if (!gesture || closingRef.current) return;
      const touch = Array.from(event.touches).find((item) => item.identifier === gesture.touchId);
      if (!touch) return;

      const deltaX = touch.clientX - gesture.startX;
      const deltaY = touch.clientY - gesture.startY;
      if (!gesture.axis) gesture.axis = classifyTouchAxis(deltaX, deltaY);
      if (gesture.axis !== 'vertical' || deltaY <= 0) return;
      if (!gesture.dragging && gesture.list?.scrollTop > 0) return;

      if (!gesture.dragging) {
        gesture.dragging = true;
        // If the list reached its top during this gesture, only the movement
        // after reaching the top should count as a sheet dismissal.
        gesture.dragStartY = gesture.startScrollTop > 0 ? touch.clientY : gesture.startY;
        sheet.classList.add('gm-post__comments-wrap--dragging');
        backdrop?.classList.add('gm-post__comments-backdrop--dragging');
      }

      if (event.cancelable) event.preventDefault();
      const dragY = Math.max(0, touch.clientY - gesture.dragStartY);
      sheet.style.setProperty('--gm-home-comments-drag-y', `${dragY}px`);
      backdrop?.style.setProperty('--gm-home-comments-backdrop-opacity', String(Math.max(0.42, 1 - (dragY / 520))));

      const elapsed = Math.max(1, event.timeStamp - gesture.lastTime);
      gesture.velocityY = (touch.clientY - gesture.lastY) / elapsed;
      gesture.lastY = touch.clientY;
      gesture.lastTime = event.timeStamp;
    };

    const onTouchEnd = (event) => {
      const gesture = dragRef.current;
      if (!gesture) return;
      const touch = Array.from(event.changedTouches).find((item) => item.identifier === gesture.touchId);
      dragRef.current = null;
      if (!gesture.dragging || !touch) {
        clearDragVisuals();
        return;
      }

      const dragY = Math.max(0, touch.clientY - gesture.dragStartY);
      const shouldClose = dragY >= Math.max(90, sheet.clientHeight * 0.18)
        || (dragY >= 44 && gesture.velocityY >= 0.7);
      if (shouldClose) {
        sheet.classList.remove('gm-post__comments-wrap--dragging');
        backdrop?.classList.remove('gm-post__comments-backdrop--dragging');
        requestClose();
        return;
      }

      clearDragVisuals();
    };

    const onTouchCancel = () => {
      dragRef.current = null;
      clearDragVisuals();
    };

    sheet.addEventListener('touchstart', onTouchStart, { passive: true });
    sheet.addEventListener('touchmove', onTouchMove, { passive: false });
    sheet.addEventListener('touchend', onTouchEnd, { passive: true });
    sheet.addEventListener('touchcancel', onTouchCancel, { passive: true });
    return () => {
      sheet.removeEventListener('touchstart', onTouchStart);
      sheet.removeEventListener('touchmove', onTouchMove);
      sheet.removeEventListener('touchend', onTouchEnd);
      sheet.removeEventListener('touchcancel', onTouchCancel);
      dragRef.current = null;
    };
  }, [isMobileViewport, open, requestClose]);

  if (!open || !isMobileViewport || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <button
        ref={backdropRef}
        type="button"
        className={`gm-post__comments-backdrop${isClosing ? ' gm-post__comments-backdrop--closing' : ''}`}
        onClick={requestClose}
        aria-label="Fechar comentários"
      />
      <div
        ref={sheetRef}
        className={`gm-post__comments-wrap gm-post__comments-wrap--open gm-post__comments-wrap--home-sheet gm-post__comments-wrap--mobile-portal${isClosing ? ' gm-post__comments-wrap--closing' : ''}${className ? ` ${className}` : ''}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Comentários"
      >
        {typeof children === 'function' ? children(requestClose) : children}
      </div>
    </>,
    document.body,
  );
}

MobileCommentsSheet.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  className: PropTypes.string,
};
